import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Platform › Apps › Client logs (Docs/PLAN_CLIENT_LOG_STREAM.md).
//
// Switch log streaming on for one app on this gateway (always expiring), then watch the events the
// app's dev builds send, live. Polls /events?after=<id> every 2 s while the tail is running.
import { useCallback, useEffect, useRef, useState } from 'react';
import { NexusAdminApiError, useNexusFetch } from '../api.js';
const LEVELS = ['debug', 'info', 'warn', 'error'];
const DURATIONS = [
    { minutes: 30, label: '30 minutes' },
    { minutes: 60, label: '1 hour' },
    { minutes: 120, label: '2 hours' },
    { minutes: 240, label: '4 hours' },
    { minutes: 480, label: '8 hours' },
    { minutes: 1440, label: '24 hours' },
];
const MAX_ROWS = 1000;
export const POLL_MS = 2000;
const LEVEL_BADGE = { debug: 'text-bg-secondary', info: 'text-bg-info', warn: 'text-bg-warning', error: 'text-bg-danger' };
/** "1h 58m left" / "12m left". Exported for the tests. */
export function timeLeft(expiresAt, now = Date.now()) {
    if (!expiresAt)
        return '';
    const ms = new Date(expiresAt).getTime() - now;
    if (ms <= 0)
        return 'expired';
    const m = Math.ceil(ms / 60_000);
    return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m left` : `${m}m left`;
}
function errText(e, fallback) {
    return e instanceof NexusAdminApiError ? `${e.code}: ${e.message}` : e instanceof Error ? e.message : fallback;
}
export function ClientLogsCard({ appId }) {
    const fetcher = useNexusFetch();
    const [setting, setSetting] = useState(null);
    const [level, setLevel] = useState('debug');
    const [scopeUser, setScopeUser] = useState('');
    const [minutes, setMinutes] = useState(120);
    const [allowNonDev, setAllowNonDev] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [events, setEvents] = useState([]);
    const [paused, setPaused] = useState(false);
    const [filterLevel, setFilterLevel] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [filterSession, setFilterSession] = useState('');
    const lastId = useRef(null);
    const loadSetting = useCallback(async () => {
        try {
            const r = await fetcher(`/v1/admin/client-logs/${encodeURIComponent(appId)}/settings`);
            const s = r?.setting ?? null;
            setSetting(s);
            if (s) {
                setLevel(s.level);
                setScopeUser(s.userId ?? '');
                setAllowNonDev(s.allowNonDev);
            }
        }
        catch (e) {
            setError(errText(e, 'could not load the client log setting'));
        }
    }, [appId, fetcher]);
    // A different app: start clean.
    useEffect(() => {
        setSetting(null);
        setEvents([]);
        setError(null);
        setPaused(false);
        lastId.current = null;
        void loadSetting();
    }, [appId, loadSetting]);
    const query = useCallback(() => {
        const p = new URLSearchParams();
        if (lastId.current)
            p.set('after', lastId.current);
        if (filterLevel)
            p.set('level', filterLevel);
        if (filterCategory.trim())
            p.set('category', filterCategory.trim());
        if (filterSession.trim())
            p.set('sessionId', filterSession.trim());
        return p.toString();
    }, [filterCategory, filterLevel, filterSession]);
    const poll = useCallback(async () => {
        try {
            const q = query();
            const r = await fetcher(`/v1/admin/client-logs/${encodeURIComponent(appId)}/events${q ? `?${q}` : ''}`);
            const got = r?.events ?? [];
            if (got.length) {
                lastId.current = got[got.length - 1].id;
                setEvents((prev) => [...prev, ...got].slice(-MAX_ROWS));
            }
        }
        catch (e) {
            setError(errText(e, 'could not load client log events'));
        }
    }, [appId, fetcher, query]);
    // Filters changed: reload the latest events with the new filters.
    useEffect(() => {
        lastId.current = null;
        setEvents([]);
    }, [filterLevel, filterCategory, filterSession, appId]);
    useEffect(() => {
        if (paused)
            return;
        void poll();
        const t = setInterval(() => void poll(), POLL_MS);
        return () => clearInterval(t);
    }, [paused, poll]);
    const save = useCallback(async (enabled) => {
        setSaving(true);
        setError(null);
        try {
            const r = await fetcher(`/v1/admin/client-logs/${encodeURIComponent(appId)}/settings`, {
                method: 'PUT',
                json: { enabled, level, userId: scopeUser.trim() ? scopeUser.trim() : null, allowNonDev, durationMinutes: minutes },
            });
            setSetting(r?.setting ?? null);
        }
        catch (e) {
            setError(errText(e, 'could not save the client log setting'));
        }
        finally {
            setSaving(false);
        }
    }, [allowNonDev, appId, fetcher, level, minutes, scopeUser]);
    const clear = useCallback(async () => {
        setError(null);
        try {
            await fetcher(`/v1/admin/client-logs/${encodeURIComponent(appId)}/events`, { method: 'DELETE' });
            setEvents([]);
        }
        catch (e) {
            setError(errText(e, 'could not clear client log events'));
        }
    }, [appId, fetcher]);
    const active = !!setting?.active;
    return (_jsxs("div", { className: "card mt-3", "data-client-logs": appId, children: [_jsxs("div", { className: "card-header d-flex align-items-center gap-2", children: [_jsx("span", { className: "fw-semibold", children: "Client logs" }), active ? (_jsxs("span", { className: "badge text-bg-success", "data-client-logs-status": "on", children: ["Streaming \u00B7 ", setting.level, " \u00B7 ", timeLeft(setting.expiresAt)] })) : (_jsx("span", { className: "badge text-bg-secondary", "data-client-logs-status": "off", children: "Off" }))] }), _jsxs("div", { className: "card-body", children: [_jsx("p", { className: "small text-body-secondary mb-3", children: "Dev builds of this app send their log lines here while streaming is on. It switches itself off at the end of the time you choose. Apps send structured fields only; card numbers, emails and tokens are removed on arrival." }), _jsxs("div", { className: "row g-2 align-items-end mb-3", children: [_jsxs("div", { className: "col-auto", children: [_jsx("label", { className: "form-label small mb-1", htmlFor: "cl-level", children: "Level" }), _jsx("select", { id: "cl-level", className: "form-select form-select-sm", value: level, onChange: (e) => setLevel(e.target.value), children: LEVELS.map((l) => _jsx("option", { value: l, children: l }, l)) })] }), _jsxs("div", { className: "col-auto", children: [_jsx("label", { className: "form-label small mb-1", htmlFor: "cl-user", children: "Only this user (id)" }), _jsx("input", { id: "cl-user", className: "form-control form-control-sm", placeholder: "All users", value: scopeUser, onChange: (e) => setScopeUser(e.target.value) })] }), _jsxs("div", { className: "col-auto", children: [_jsx("label", { className: "form-label small mb-1", htmlFor: "cl-duration", children: "For" }), _jsx("select", { id: "cl-duration", className: "form-select form-select-sm", value: minutes, onChange: (e) => setMinutes(Number(e.target.value)), children: DURATIONS.map((d) => _jsx("option", { value: d.minutes, children: d.label }, d.minutes)) })] }), _jsxs("div", { className: "col-auto form-check ms-2", children: [_jsx("input", { id: "cl-nondev", type: "checkbox", className: "form-check-input", checked: allowNonDev, onChange: (e) => setAllowNonDev(e.target.checked) }), _jsx("label", { className: "form-check-label small", htmlFor: "cl-nondev", children: "Also accept non-dev builds" })] }), _jsxs("div", { className: "col-auto ms-auto d-flex gap-2", children: [_jsx("button", { type: "button", className: "btn btn-sm btn-primary", disabled: saving, onClick: () => void save(true), children: active ? 'Update' : 'Start streaming' }), active && (_jsx("button", { type: "button", className: "btn btn-sm btn-outline-secondary", disabled: saving, onClick: () => void save(false), children: "Stop streaming" }))] })] }), error && _jsx("div", { className: "alert alert-danger py-2 small", children: error }), _jsxs("div", { className: "d-flex flex-wrap gap-2 align-items-center mb-2", children: [_jsxs("select", { "aria-label": "Filter level", className: "form-select form-select-sm w-auto", value: filterLevel, onChange: (e) => setFilterLevel(e.target.value), children: [_jsx("option", { value: "", children: "All levels" }), LEVELS.map((l) => _jsxs("option", { value: l, children: [l, "+"] }, l))] }), _jsx("input", { "aria-label": "Filter category", className: "form-control form-control-sm w-auto", placeholder: "Category starts with\u2026", value: filterCategory, onChange: (e) => setFilterCategory(e.target.value) }), _jsx("input", { "aria-label": "Filter session", className: "form-control form-control-sm w-auto", placeholder: "Session id", value: filterSession, onChange: (e) => setFilterSession(e.target.value) }), _jsx("button", { type: "button", className: "btn btn-sm btn-outline-secondary", onClick: () => setPaused((p) => !p), children: paused ? 'Resume' : 'Pause' }), _jsx("button", { type: "button", className: "btn btn-sm btn-outline-danger", onClick: () => void clear(), children: "Clear" }), _jsxs("span", { className: "small text-body-secondary ms-auto", children: [events.length, " events"] })] }), _jsx("div", { className: "border rounded small font-monospace", style: { maxHeight: 420, overflowY: 'auto' }, "data-client-logs-tail": true, children: events.length === 0 ? (_jsx("div", { className: "p-3 text-body-secondary", children: "No events yet." })) : (_jsx("table", { className: "table table-sm mb-0", children: _jsx("tbody", { children: events.map((e) => (_jsxs("tr", { "data-client-log-id": e.id, children: [_jsx("td", { className: "text-nowrap text-body-secondary", children: new Date(e.ts).toLocaleTimeString() }), _jsx("td", { children: _jsx("span", { className: `badge ${LEVEL_BADGE[e.level]}`, children: e.level }) }), _jsx("td", { className: "text-nowrap", children: e.category }), _jsxs("td", { children: [e.message, e.fields && (_jsxs("span", { className: "text-body-secondary", children: [' ', Object.entries(e.fields).map(([k, v]) => `${k}=${String(v)}`).join(' ')] }))] }), _jsx("td", { className: "text-nowrap text-body-secondary", title: [e.device?.model, e.device?.os, e.device?.appVersion && `v${e.device.appVersion}`, e.device?.build && `(${e.device.build})`, e.channel].filter(Boolean).join(' '), children: e.sessionId.slice(0, 8) })] }, e.id))) }) })) })] })] }));
}
//# sourceMappingURL=ClientLogsCard.js.map